import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import userPreRegistration from "@/services/UserPreRegistration";
import { useAtom } from "jotai";
import { registerPopup } from "@/Atoms";

const FreeTrialStartup = ({
  openPurchasePopup,
  setOpenPurchasePopup,
  startingQuestion = "q0",
  onOpenStartupRegistration = null,
  onOpenContentAccess = null,
  onOpenRegister = null
}) => {
  const router = useRouter();
  const [openRegisterPopup, setOpenRegisterPopup] = useAtom(registerPopup);

  // Form data collection
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // Current step tracker
  const [currentStep, setCurrentStep] = useState("initial");
  const [questionHistory, setQuestionHistory] = useState([]);
  const [targetQuestion, setTargetQuestion] = useState(startingQuestion);
  const [registrationId, setRegistrationId] = useState(null);

  // Answer states
  const [selected1, setSelected1] = useState(""); // 0. WHICH ONE BEST DESCRIBES YOU?
  const [selected2, setSelected2] = useState(""); // QX. WHAT KIND OF INVESTOR ARE YOU?
  const [selected3, setSelected3] = useState(""); // QZ. HAVE YOU ACTIVELY INVESTED IN MIDDLE EAST & AFRICAN STARTUPS BEFORE?
  const [selected4, setSelected4] = useState(""); // 1. ARE YOU THE FOUNDER OF YOUR STARTUP?
  const [selected5, setSelected5] = useState(""); // 2.1 ARE YOU PLANNING ON LAUNCHING A TECH STARTUP SOON?
  const [selected6, setSelected6] = useState(""); // 2.2 HAS YOUR STARTUP SUCCESSFULLY FUNDRAISED BEFORE?
  const [selected7, setSelected7] = useState(""); // 3. ARE YOU LOOKING TO FUNDRAISE IN THE FUTURE?
  const [selected8, setSelected8] = useState(""); // 4. WHEN DO YOU THINK YOU'LL START YOUR FUNDRAISING PROCESS?

  const options1 = [
    { id: "startup", label: "I'm a Startup!" },
    { id: "investor", label: "I'm an Investor" },
    { id: "curious", label: "I'm just Curious" },
  ];
  const options2 = [
    { id: "angel", label: "Angel Investor" },
    { id: "vc", label: "Venture Capital" },
    { id: "fund", label: "Fund of Funds" },
    { id: "family", label: "Family Office" },
    { id: "private", label: "Private Equity" },
    { id: "other", label: "Other" },
  ];
  const options3 = [
    { id: "yes", label: "Yes" },
    { id: "no", label: "No" },
  ];
  const options4 = [
    { id: "yes", label: "Yes, likely within the next 2 years" },
    { id: "no", label: "No plans yet" },
  ];
  const options5 = [
    { id: "yes", label: "Within the Next 12 months" },
    { id: "no", label: "Likely after 1 Year+" },
  ];

  // Reset modal state when it opens or startingQuestion changes
  useEffect(() => {
    if (openPurchasePopup) {
      // Reset all state when modal opens
      setCurrentStep("initial");
      setQuestionHistory([]);
      setTargetQuestion(startingQuestion);
      setRegistrationId(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
      });
      setSelected1("");
      setSelected2("");
      setSelected3("");
      setSelected4("");
      setSelected5("");
      setSelected6("");
      setSelected7("");
      setSelected8("");
    }
  }, [openPurchasePopup, startingQuestion]);

  // Save or update registration data
  const saveRegistrationData = async (stepData) => {
    try {
      // Always save with full data - API handles create/update automatically
      const id = await userPreRegistration.save({
        full_name: formData.name,
        email: formData.email,
        phone_number: formData.phone,
        steps: stepData,
      });

      if (id && !registrationId) {
        setRegistrationId(id);
      }
    } catch (error) {
      console.error('Error saving registration data:', error);
    }
  };

  // Collect current step data
  const getCurrentStepData = () => {
    const stepData = {};

    if (selected1) stepData.q0_user_type = selected1;
    if (selected2) stepData.qx_investor_type = selected2;
    if (selected3) stepData.qz_invested_mena = selected3;
    if (selected4) stepData.q1_is_founder = selected4;
    if (selected5) stepData.q2_1_launch_startup = selected5;
    if (selected6) stepData.q2_2_fundraised_before = selected6;
    if (selected7) stepData.q3_looking_to_fundraise = selected7;
    if (selected8) stepData.q4_fundraising_timeline = selected8;

    return stepData;
  };

  // Navigation functions
  const handleNext = async () => {
    const history = [...questionHistory, currentStep];
    setQuestionHistory(history);

    // Question routing logic
    if (currentStep === "initial") {
      // Save initial registration data
      const stepData = getCurrentStepData();
      await saveRegistrationData(stepData);

      // After initial form, go to the target question
      setCurrentStep(targetQuestion);

      // If starting at Q1, auto-select "startup" for Q0
      if (targetQuestion === "q1") {
        setSelected1("startup");
      }
      // If starting at QX, auto-select "investor" for Q0
      else if (targetQuestion === "qx") {
        setSelected1("investor");
      }
    } else {
      // Save step data before navigation
      const stepData = getCurrentStepData();
      await saveRegistrationData(stepData);
    }

    // Continue with routing logic
    if (currentStep === "q0") {
      if (selected1 === "startup") {
        setCurrentStep("q1");
      } else if (selected1 === "investor") {
        setCurrentStep("qx");
      } else if (selected1 === "curious") {
        setCurrentStep("q2_1");
      }
    } else if (currentStep === "qx") {
      if (selected2 === "angel") {
        // Navigate to Angel Investor Registration
        router.push("/angel-investors/register");
      } else if (selected2 === "vc" || selected2 === "fund") {
        // Navigate to Investor Claim Page
        router.push("/investors/claim");
      } else if (selected2 === "family" || selected2 === "private" || selected2 === "other") {
        setCurrentStep("qz");
      }
    } else if (currentStep === "qz") {
      if (selected3 === "yes") {
        // Navigate to Investor Claim Page
        router.push("/investors/claim");
      } else {
        // Recommend ALL ACCESS PASS
        setOpenPurchasePopup(false);
        if (onOpenContentAccess) {
          onOpenContentAccess({
            fullName: formData.name,
            email: formData.email,
            phone: formData.phone
          });
        }
      }
    } else if (currentStep === "q1") {
      // Either Yes or No goes to Question 2.2
      setCurrentStep("q2_2");
    } else if (currentStep === "q2_1") {
      if (selected5 === "yes") {
        // Recommend ALL ACCESS PASS
        setOpenPurchasePopup(false);
        if (onOpenContentAccess) {
          onOpenContentAccess({
            fullName: formData.name,
            email: formData.email,
            phone: formData.phone
          });
        }
      } else {
        // Free registration
        setOpenPurchasePopup(false);
        setOpenRegisterPopup(true);
      }
    } else if (currentStep === "q2_2") {
      // Regardless of answer, go to Q3
      setCurrentStep("q3");
    } else if (currentStep === "q3") {
      if (selected7 === "yes") {
        setCurrentStep("q4");
      } else {
        // Recommend ALL ACCESS PASS
        setOpenPurchasePopup(false);
        if (onOpenContentAccess) {
          onOpenContentAccess({
            fullName: formData.name,
            email: formData.email,
            phone: formData.phone
          });
        }
      }
    } else if (currentStep === "q4") {
      if (selected8 === "yes") {
        // Open STARTUP REGISTRATION form with pre-filled data
        setOpenPurchasePopup(false);
        if (onOpenStartupRegistration) {
          onOpenStartupRegistration({
            fullName: formData.name,
            email: formData.email,
            phone: formData.phone
          });
        }
      } else {
        // Recommend ALL ACCESS PASS
        setOpenPurchasePopup(false);
        if (onOpenContentAccess) {
          onOpenContentAccess({
            fullName: formData.name,
            email: formData.email,
            phone: formData.phone
          });
        }
      }
    }
  };

  const handleBack = () => {
    if (questionHistory.length > 0) {
      const previousStep = questionHistory[questionHistory.length - 1];
      setCurrentStep(previousStep);
      setQuestionHistory(questionHistory.slice(0, -1));
    }
  };

  // Check if current step can proceed
  const canProceed = () => {
    if (currentStep === "initial") {
      return formData.name && formData.email && formData.phone;
    } else if (currentStep === "q0") {
      return selected1 !== "";
    } else if (currentStep === "qx") {
      return selected2 !== "";
    } else if (currentStep === "qz") {
      return selected3 !== "";
    } else if (currentStep === "q1") {
      return selected4 !== "";
    } else if (currentStep === "q2_1") {
      return selected5 !== "";
    } else if (currentStep === "q2_2") {
      return selected6 !== "";
    } else if (currentStep === "q3") {
      return selected7 !== "";
    } else if (currentStep === "q4") {
      return selected8 !== "";
    }
    return false;
  };

  // Get current question number for display
  const getQuestionNumber = () => {
    const stepMap = {
      initial: null,
      q0: null,
      qx: 2,
      qz: 3,
      q1: 1,
      q2_1: 2,
      q2_2: 2,
      q3: 3,
      q4: 4,
    };
    return stepMap[currentStep];
  };

  const getTotalQuestions = () => {
    // This varies based on path, but we'll show a general estimate
    if (selected1 === "investor") return 3;
    if (selected1 === "curious") return 2;
    return 4; // startup path
  };

  // Render current question
  const renderQuestion = () => {
    if (currentStep === "initial") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              Let&apos;s Get Started
            </h3>
            <p className='text-sm text-gray-600'>
              Please provide your contact information to continue
            </p>
          </div>

          <div className='grid gap-4 mt-6 lg:grid-cols-1'>
            <div className='space-y-1.5'>
              <Label className='text-sm text-gray-600'>
                Full Name<span className='text-red-700'>*</span>
              </Label>
              <Input
                type='text'
                placeholder='Enter your full name'
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className='w-full bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
              />
            </div>
            <div className='space-y-1.5'>
              <Label className='text-sm text-gray-600'>
                Email<span className='text-red-700'>*</span>
              </Label>
              <Input
                type='email'
                placeholder='Enter your email'
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className='w-full bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
              />
            </div>
            <div className='space-y-1.5'>
              <Label className='text-sm text-gray-600'>
                Phone Number<span className='text-red-700'>*</span>
              </Label>
              <Input
                type='tel'
                placeholder='Enter your phone number'
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className='w-full bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:border-gray-300'
              />
            </div>
          </div>
        </div>
      );
    }

    if (currentStep === "q0") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4 lg:items-end lg:justify-between lg:flex-row'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              Which One Best Describes You?
            </h3>
            <span className='text-xs opacity-40 shrink-0'>
              Choose one option
            </span>
          </div>

          <div className='grid gap-2 mt-4 lg:grid-cols-3'>
            {options1.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => setSelected1(option.id)}
                className={`p-4 flex items-center text-sm justify-center h-12 lg:h-24 font-medium leading-tight text-center border shadow-sm rounded-lg transition-colors duration-200
                ${
                  selected1 === option.id
                    ? "bg-orange-1/10 text-orange-600 border-orange-1"
                    : "border-background/10 hover:border-black/20"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === "qx") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4 lg:items-end lg:justify-between lg:flex-row'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              What Kind of Investor are You?
            </h3>
            <span className='text-xs opacity-40 shrink-0'>
              Choose one option
            </span>
          </div>

          <div className='grid gap-2 mt-4 lg:grid-cols-3'>
            {options2.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => setSelected2(option.id)}
                className={`p-4 flex items-center text-sm justify-center h-12 lg:h-24 font-medium leading-tight text-center border shadow-sm rounded-lg transition-colors duration-200
                ${
                  selected2 === option.id
                    ? "bg-orange-1/10 text-orange-600 border-orange-1"
                    : "border-background/10 hover:border-black/20"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === "qz") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4 lg:items-end lg:justify-between lg:flex-row'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              Have You Actively Invested in Middle East & African Startups
              Before?
            </h3>
            <span className='text-xs opacity-40 shrink-0'>
              Choose one option
            </span>
          </div>

          <div className='grid grid-cols-2 gap-2 mt-4'>
            {options3.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => setSelected3(option.id)}
                className={`p-4 flex items-center text-base lg:text-xl justify-center h-12 lg:h-24 font-medium leading-tight text-center border shadow-sm rounded-lg transition-colors duration-200
                ${
                  selected3 === option.id
                    ? "bg-orange-1/10 text-orange-600 border-orange-1"
                    : "border-background/10 hover:border-black/20"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === "q1") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4 lg:items-end lg:justify-between lg:flex-row'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              Are You the Founder of Your Startup?
            </h3>
            <span className='text-xs opacity-40 shrink-0'>
              Choose one option
            </span>
          </div>

          <div className='grid grid-cols-2 gap-2 mt-4'>
            {options3.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => setSelected4(option.id)}
                className={`p-4 flex items-center text-base lg:text-xl justify-center h-12 lg:h-24 font-medium leading-tight text-center border shadow-sm rounded-lg transition-colors duration-200
                ${
                  selected4 === option.id
                    ? "bg-orange-1/10 text-orange-600 border-orange-1"
                    : "border-background/10 hover:border-black/20"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === "q2_1") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4 lg:items-end lg:justify-between lg:flex-row'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              Are You Planning on Launching a Tech Startup Soon?
            </h3>
            <span className='text-xs opacity-40 shrink-0'>
              Choose one option
            </span>
          </div>

          <div className='grid gap-2 mt-4 lg:grid-cols-2'>
            {options4.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => setSelected5(option.id)}
                className={`p-4 flex items-center text-sm justify-center h-12 lg:h-24 font-medium leading-tight text-center border shadow-sm rounded-lg transition-colors duration-200
                ${
                  selected5 === option.id
                    ? "bg-orange-1/10 text-orange-600 border-orange-1"
                    : "border-background/10 hover:border-black/20"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === "q2_2") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4 lg:items-end lg:justify-between lg:flex-row'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              Has Your Startup Successfully Fundraised Before?
            </h3>
            <span className='text-xs opacity-40 shrink-0'>
              Choose one option
            </span>
          </div>

          <div className='grid grid-cols-2 gap-2 mt-4'>
            {options3.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => setSelected6(option.id)}
                className={`p-4 flex items-center text-base lg:text-xl justify-center h-12 lg:h-24 font-medium leading-tight text-center border shadow-sm rounded-lg transition-colors duration-200
                ${
                  selected6 === option.id
                    ? "bg-orange-1/10 text-orange-600 border-orange-1"
                    : "border-background/10 hover:border-black/20"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === "q3") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4 lg:items-end lg:justify-between lg:flex-row'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              Are You Looking to Fundraise in the Future?
            </h3>
            <span className='text-xs opacity-40 shrink-0'>
              Choose one option
            </span>
          </div>

          <div className='grid grid-cols-2 gap-2 mt-4'>
            {options3.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => setSelected7(option.id)}
                className={`p-4 flex items-center text-base lg:text-xl justify-center h-12 lg:h-24 font-medium leading-tight text-center border shadow-sm rounded-lg transition-colors duration-200
                ${
                  selected7 === option.id
                    ? "bg-orange-1/10 text-orange-600 border-orange-1"
                    : "border-background/10 hover:border-black/20"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === "q4") {
      return (
        <div>
          <div className='flex flex-col gap-2 lg:gap-4 lg:items-end lg:justify-between lg:flex-row'>
            <h3 className='text-xl font-medium leading-tight font-display'>
              When Do You Think You&apos;ll Start Your Fundraising Process?
            </h3>
            <span className='text-xs opacity-40 shrink-0'>
              Choose one option
            </span>
          </div>

          <div className='grid gap-2 mt-4 lg:grid-cols-2'>
            {options5.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => setSelected8(option.id)}
                className={`p-4 flex items-center text-sm justify-center h-12 lg:h-24 font-medium leading-tight text-center border shadow-sm rounded-lg transition-colors duration-200
                ${
                  selected8 === option.id
                    ? "bg-orange-1/10 text-orange-600 border-orange-1"
                    : "border-background/10 hover:border-black/20"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={!!openPurchasePopup} onOpenChange={setOpenPurchasePopup}>
      <DialogContent
        className={`max-w-2xl p-0 bg-white text-background`}
        hideCloseButton
      >
        <ScrollArea className='max-h-[100dvh]'>
          <div>
            <div className='p-5 text-center lg:p-8'>
              <h2 className='text-2xl font-semibold leading-none tracking-tight lg:text-3xl font-display'>
                We Need to &apos;Get to Know&apos; our Customer to Tailor our offerings…
              </h2>
              <p className='mt-3 text-sm'>
                Answer a few questions for us to get to know you better, so we can tailor our offering to you.
              </p>

              <div className='pt-5 mt-5 text-left border-t lg:pt-8 lg:mt-8 border-background/10'>
                {renderQuestion()}
              </div>
            </div>

            <div className='flex items-center justify-between gap-8 p-3 mt-4 border-t border-background/10'>
              <Button
                onClick={
                  questionHistory.length > 0
                    ? handleBack
                    : () => setOpenPurchasePopup(false)
                }
                className='bg-background/5 hover:bg-background/10 text-background'
              >
                {questionHistory.length > 0 ? "Back" : "Cancel"}
              </Button>
              <span className='text-sm opacity-50'>
                {currentStep === "initial" || currentStep === "q0"
                  ? "Getting Started"
                  : `Question ${getQuestionNumber()} of ${getTotalQuestions()}`}
              </span>
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className='text-white bg-black disabled:bg-black/40 hover:bg-black/50'
              >
                Proceed
              </Button>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default FreeTrialStartup;
